/**
 * One skill in the Step 1 source picker.
 *
 * Shows the skill name, the capability label, the readiness badge, and (if
 * the skill needs setup) a "Connect" CTA that deep-links to the Skills
 * Library detail page. Clicking the card body selects the skill and
 * advances the wizard to Step 2.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { FiCheckCircle, FiAlertTriangle, FiExternalLink } from 'react-icons/fi';

import IconWrapper from '../../../ui/IconWrapper';
import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import {
  computeCapabilityReadiness,
  type ImportCapability,
  type ImportCapabilitySkill,
} from '../../../../types/importCapabilities';

interface SourceCardProps {
  skill: ImportCapabilitySkill;
  capability: ImportCapability;
  onSelect: (skill: ImportCapabilitySkill, capability: ImportCapability) => void;
}

const SourceCard: React.FC<SourceCardProps> = ({ skill, capability, onSelect }) => {
  // Readiness is per-capability: only the secrets THIS capability requires
  // should determine its badge, not every secret the skill declares.
  const readiness = computeCapabilityReadiness(
    capability.requires_secrets,
    skill.secret_statuses,
  );
  const isReady = readiness === 'ready';
  // Skills Library is a single page; passing ?selected=<skill_name> tells
  // SkillsView to auto-open that skill's detail so the admin lands inside
  // the credential setup flow rather than scrolling through the catalog.
  const skillsLibraryLink = `/v3/settings/skills?selected=${encodeURIComponent(skill.skill_name)}`;

  // Only the Ready card body is interactive; the Needs-setup card renders as
  // a plain container so the Setup Link inside is the only focusable element
  // (avoids nesting interactive controls — invalid a11y / ambiguous keyboard
  // behavior).
  const interactiveProps = isReady
    ? {
        role: 'button' as const,
        tabIndex: 0,
        onClick: () => onSelect(skill, capability),
        onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect(skill, capability);
          }
        },
      }
    : {};

  return (
    <div
      {...interactiveProps}
      className={[
        'rounded-lg border bg-white p-4 transition-shadow',
        isReady
          ? 'cursor-pointer border-slate-200 hover:border-slate-400 hover:shadow-sm'
          : 'border-slate-200 opacity-90',
      ].join(' ')}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="truncate text-base font-semibold text-slate-900">
              {skill.name || skill.skill_name}
            </h3>
            {isReady ? (
              <Badge variant="default" className="gap-1 bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                <IconWrapper icon={FiCheckCircle} className="h-3 w-3" />
                Ready
              </Badge>
            ) : (
              <Badge variant="default" className="gap-1 bg-amber-100 text-amber-700 hover:bg-amber-100">
                <IconWrapper icon={FiAlertTriangle} className="h-3 w-3" />
                {readiness === 'partial' ? 'Partially configured' : 'Needs setup'}
              </Badge>
            )}
          </div>
          <p className="mt-1 text-sm text-slate-600">{capability.label}</p>
          {capability.description ? (
            <p className="mt-2 text-xs text-slate-500">{capability.description}</p>
          ) : null}
        </div>
      </div>

      {!isReady ? (
        <div className="mt-3 flex items-center justify-between gap-3 rounded-md bg-amber-50 p-3">
          <p className="text-xs text-amber-900">
            Configure {skill.name || skill.skill_name}'s credentials before
            connecting it as a source.
          </p>
          {/* Render the Link as the Button itself (asChild) so the DOM
              produces a single <a>, not nested <a><button>. */}
          <Button
            asChild
            variant="outline"
            size="sm"
            className="border-amber-300 text-amber-900 hover:bg-amber-100"
          >
            <Link to={skillsLibraryLink}>
              Set up
              <IconWrapper icon={FiExternalLink} className="ml-1 h-3 w-3" />
            </Link>
          </Button>
        </div>
      ) : null}
    </div>
  );
};

export default SourceCard;
